import React, {
  RefObject,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import {
  Dayjs,
  getOrgNow,
} from 'utils/dayjs';
import {
  useFormContext,
} from 'react-hook-form';
import pick from 'lodash/pick';
import values from 'lodash/values';
import isNil from 'lodash/isNil';
import {
  InsuranceDTO,
  PatientDTO,
  InsuranceMSPTypeSettingDTO,
  PatientSummaryDTO,
} from 'dtos';
import {
  InsuranceSlot,
} from 'dtos/insuranceSlot';
import Controller from 'core/components/FormController';
import {
  isEmptyString,
} from '@populateui/utils';
import Accordion from 'core/components/Accordion';
import Input from 'pages/Dashboard/components/Input';
import Select from 'core/components/Select';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import AddressFields, {
  AddressForm,
} from 'pages/Dashboard/components/AddressFields';
import DemographicFields, {
  DemographicsForm,
} from 'pages/Dashboard/components/DemographicFields';
import PhoneInput from 'core/components/PhoneInput';
import {
  validatePhone,
} from 'pages/Dashboard/utils/helper';
import {
  getPatientDemographics,
} from 'pages/Dashboard/utils/patient';
import DemographicsHeader from 'pages/Dashboard/components/DemographicsHeader';
import InsuranceLookup from 'pages/Dashboard/components/InsuranceLookup';
import {
  Payer,
} from 'pages/Dashboard/types/insurance';
import CurrencyTextField from 'core/components/CurrencyTextField';
import {
  validateAddressInfo,
} from 'utils/react-hook-form';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Divider from '@mui/material/Divider';
import ToolTip from 'core/components/Tooltip';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import Button from 'pages/Dashboard/components/Button';
import head from 'lodash/head';
import {
  useInsuranceMetaData,
} from 'views/EMR/components/Insurance/hooks';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import {
  getDefaultInactiveEffectiveTill,
  REQUIRED_ADDRESS_FIELDS,
  validateEffectiveFrom,
  validateEffectiveTill,
} from 'views/EMR/components/Insurance/utils';
import {
  getInsuranceBasicInfo,
} from 'utils/misc';
import {
  InsuranceAttachmentFormState,
} from 'views/EMR/components/Insurance/Dialog/AssociatedInsuranceCardsPanel/useAssociatedInsuranceCards';

export type Demographics = Pick<PatientDTO,
  'firstName'
  | 'middleName'
  | 'lastName'
  | 'genderDescription'
  | 'formattedNames'
> & { dateOfBirth: Dayjs };

export type InsuranceDataForm = Omit<InsuranceDTO,
  'insuranceOwnerDOB'
  | 'mspType'
  | 'effectiveFrom'
  | 'effectiveTill'
> & {
  attachmentState: InsuranceAttachmentFormState;
  demographics?: Demographics;
  insuranceOwnerDOB?: Dayjs;
  effectiveFrom?: Dayjs;
  effectiveTill?: Dayjs | null;
  mspType?: InsuranceMSPTypeSettingDTO;
};

type Props = {
  patientSummary?: PatientSummaryDTO;
  defaultValues?: InsuranceDataForm;
  isLoading?: boolean;
};

const commonAccordionProps = {
  expandIcon: null,
  disableGutters: true,
  className: '!border-0',
  headerClasses: {
    root: '!p-0 !text-xs font-semibold !border-0 !min-h-[initial]',
    content: '!m-0 !mb-2',
  },
  detailsClasses: { root: '!p-0' },
};

export default function InsuranceForm({
  patientSummary,
  defaultValues,
  isLoading,
}: Props) {
  const {
    control,
    watch,
    reset,
    trigger,
    setValue,
    formState: { isDirty },
  } = useFormContext<InsuranceDataForm>();

  const containerRef = useRef<HTMLDivElement>(null);
  const insuranceLookupRef = useRef<HTMLInputElement>(null);

  const formValue = watch();

  const {
    insuranceOwnerRelationshipType,
    demographics,
  } = formValue;

  const { address, phoneNumber } = patientSummary ?? {};
  const hasAddress = !isNil(address) && values(
    pick(address, REQUIRED_ADDRESS_FIELDS),
  ).every((item) => !isEmptyString(item));
  const hasPhoneNumber = !isEmptyString(phoneNumber?.phoneNumberWithAreaCode);

  const {
    relationshipTypes,
    relationshipTypesByName,
    mspTypes,
    insuranceSlotValues,
    getInsuranceSlotLabel,
  } = useInsuranceMetaData();

  const {
    isSubscriberPatient,
    isEmployerInsurance,
  } = relationshipTypesByName[insuranceOwnerRelationshipType ?? ''] ?? {};

  const isSelf = isSubscriberPatient || isEmployerInsurance;

  const mspOptions = useMemo(() => ([
    { code: null, name: 'None' },
    ...(mspTypes ?? []),
  ]), [mspTypes]);

  useEffect(() => {
    if (isNil(insuranceOwnerRelationshipType)) {
      setValue('insuranceOwnerRelationshipType', head(relationshipTypes ?? []));
    }
  }, [relationshipTypes]);

  const updateDemographics = useCallback(() => {
    const newDemographics = isSelf
      ? getPatientDemographics(patientSummary ?? {})
      : demographics;
    reset({
      ...formValue,
      demographics: newDemographics,
      insuranceOwnerPhoneNumber: isSelf && hasPhoneNumber
        ? phoneNumber?.phoneNumberWithAreaCode
        : formValue.insuranceOwnerPhoneNumber,
    }, { keepErrors: true });
  }, [isSelf, demographics, patientSummary, formValue, hasPhoneNumber, phoneNumber]);

  useEffect(() => {
    if (isDirty) {
      updateDemographics();
    }
  }, [isDirty, isSelf]);

  const [selectedPayer, selectPayer] = useState<Payer | null>(defaultValues?.payerEntry ?? null);
  const basicInfo = useMemo(
    () => getInsuranceBasicInfo(selectedPayer ?? {}, true),
    [selectedPayer],
  );

  const triggerEffectiveDateValidation = useCallback(() => {
    trigger(['effectiveFrom', 'effectiveTill', 'inactive']);
  }, [trigger]);

  return (
    <div
      className="flex flex-col gap-form-row w-full populate-form"
      ref={containerRef as RefObject<HTMLDivElement>}
    >
      <div className="grid grid-cols-3 gap-form-field">
        <Controller
          control={control}
          name="insuranceOwnerRelationshipType"
          rules={{ required: true }}
          render={({ field, fieldState: { invalid } }) => (
            <Select<string>
              {...field}
              error={invalid}
              showErrorText={false}
              getValue={(option) => option}
              getLabel={(option) => option}
              options={relationshipTypes ?? []}
              placeholder="Select"
              className="min-w-0"
              id="insurance-type"
              muiClasses={{ select: '!py-[7px]' }}
              label="Relationship to Subscriber"
            />
          )}
        />
        {isEmployerInsurance && (
          <Controller
            name="employerName"
            control={control}
            rules={{ required: 'Employer is required' }}
            render={({ field, fieldState: { invalid } }) => (
              <Input
                {...field}
                required
                error={invalid}
                showErrorText={false}
                id="subscriber-employer"
                className="min-w-0"
                label="Employer"
                type="text"
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="mspType"
          render={({ field }) => (
            <Select<InsuranceMSPTypeSettingDTO>
              {...field}
              value={typeof field.value === 'object' ? field.value : { code: field.value }}
              getValue={(option) => option?.code ?? '--'}
              getLabel={(option) => (
                !isNil(option?.code)
                  ? `${option?.code ?? '--'} - ${option?.name ?? '--'}`
                  : 'None'
              )}
              options={mspOptions}
              placeholder="Select"
              className="min-w-0"
              id="msp-type"
              muiClasses={{ select: '!py-[7px]' }}
              label="MSP Type"
            />
          )}
        />
      </div>
      <Controller
        control={control}
        name="demographics"
        render={({ field, fieldState: { error } }) => (
          <Accordion
            expanded={!isSelf}
            defaultExpanded={false}
            header={isSelf
              ? (
                <DemographicsHeader
                  demographics={(field?.value as Demographics) ?? {}}
                  insuranceOwnerAddress={address ?? {}}
                  insuranceOwnerPhoneNumber={phoneNumber?.phoneNumberWithAreaCode}
                  hasAddress={hasAddress}
                  isLoading={isLoading}
                />
              )
              : 'Subscriber\'s Demographic Information'}
            {...commonAccordionProps}
          >
            <DemographicFields
              key={insuranceOwnerRelationshipType}
              {...field}
              value={field.value as DemographicsForm}
              error={error}
              disabled={isSelf}
            />
          </Accordion>
        )}
      />
      {(!isSelf || !hasAddress) && !isLoading && (
        <Controller
          control={control}
          name="insuranceOwnerAddress"
          rules={{ validate: (value) => (validateAddressInfo(value) || 'Provide valid address') }}
          render={({ field }) => (
            <Accordion
              expanded
              header="Subscriber's Address"
              {...commonAccordionProps}
            >
              <AddressFields
                {...field}
                value={field.value as AddressForm}
                disabled={isSelf && hasAddress}
              />
            </Accordion>
          )}
        />
      )}
      {(!isSelf || !hasPhoneNumber) && !isLoading && (
        <div className="flex gap-form-field w-full">
          <Controller
            name="insuranceOwnerPhoneNumber"
            control={control}
            rules={{
              validate: (value) => (validatePhone(value?.toString()) ? true : 'Subscriber\'s Phone is required'),
            }}
            render={({ field, fieldState: { error } }) => (
              <Wrapper
                className="w-1/3"
                error={!isNil(error)}
                label="Subscriber's Phone"
                id="subscribers-phone"
                showErrorText={false}
                required
              >
                <PhoneInput
                  {...field}
                  error={!isNil(error)}
                  id="phone"
                  name="phone"
                />
              </Wrapper>
            )}
          />
        </div>
      )}
      <div className="flex gap-form-field w-full items-baseline">
        <Controller
          name="insuranceName"
          control={control}
          rules={{ required: 'Payer is required' }}
          render={({ field, fieldState: { invalid } }) => (
            <div className="flex flex-col flex-1">
              <InsuranceLookup
                {...field}
                onChange={(value: Payer) => {
                  field.onChange(value?.payerName ?? '');
                  if (!isNil(value?.payerEntryId)) {
                    setValue('payerEntryId', value?.payerEntryId);
                  }
                  selectPayer(value);
                }}
                required
                error={invalid}
                id="insurance-name"
                className="!w-full !mb-0"
                inputRef={insuranceLookupRef as RefObject<HTMLInputElement>}
              />
              {!isNil(selectedPayer) && (
                <div className="text-[10px] font-semibold text-gray-600">
                  {basicInfo}
                </div>
              )}
            </div>
          )}
        />
      </div>
      <div className="flex gap-form-field w-full items-end">
        <Controller
          name="memberNumber"
          rules={{ required: 'Member ID is required' }}
          control={control}
          render={({ field: { onChange, ...field }, fieldState: { error } }) => (
            <Input
              {...field}
              required
              error={!isNil(error)}
              showErrorText={false}
              className="w-2/3"
              id="insurance-member-number"
              label="Member ID"
              type="text"
              inputProps={{ maxLength: 64 }}
              onChange={(value) => {
                onChange(value?.replace(/[^A-Za-z0-9]/g, ''));
              }}
            />
          )}
        />
        <Controller
          name="copay"
          control={control}
          render={({ field }) => (
            <Wrapper
              id="insurance-co-pay"
              className="w-1/3"
              label="Copay"
            >
              <CurrencyTextField
                {...field}
                value={field.value ?? ''}
                allowNegative
              />
            </Wrapper>
          )}
        />
      </div>
      <div className="flex gap-form-field w-full">
        <Controller
          name="groupName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="insurance-group-name"
              className="w-1/3"
              label="Group Name"
              type="text"
              inputProps={{ maxLength: 64 }}
            />
          )}
        />
        <Controller
          name="groupNumber"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="insurance-group-id"
              className="w-1/3"
              label="Group Number"
              type="text"
              inputProps={{ maxLength: 64 }}
            />
          )}
        />
        <Controller
          name="policyNumber"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="insurance-policy-number"
              className="w-1/3"
              label="Policy Number"
              type="text"
              inputProps={{ maxLength: 64 }}
            />
          )}
        />
      </div>
      <div className="flex gap-form-field w-full items-end">
        <Controller
          control={control}
          name="effectiveFrom"
          rules={{
            validate: validateEffectiveFrom,
            required: 'Effective from date is required',
            deps: ['effectiveTill'],
          }}
          render={({ field, fieldState: { invalid, error } }) => (
            <Wrapper
              id="effective-from"
              error={invalid}
              errorText={error?.message?.toString()}
              required
              showErrorText={false}
              label="Effective from"
              className="w-1/2"
            >
              <DatePicker
                {...field}
                value={field.value ?? null}
                containerClassName="w-full"
                className="w-full"
                inputContainerClassName="w-full"
                showDateButtons={false}
                disableFuture
                maxDate={getOrgNow().startOf('day')}
              />
            </Wrapper>
          )}
        />
        <Controller
          control={control}
          name="effectiveTill"
          rules={{
            validate: validateEffectiveTill,
            deps: ['effectiveFrom'],
          }}
          render={({ field, fieldState: { invalid, error } }) => (
            isNil(field.value)
              ? (
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => {
                    setValue('effectiveTill', getDefaultInactiveEffectiveTill(), {
                      shouldDirty: true,
                    });
                    setValue('inactive', true, { shouldDirty: true });
                    triggerEffectiveDateValidation();
                  }}
                  className="gap-1 flex items-center !h-6 !relative -top-1"
                >
                  <AddIcon className="!w-4 !h-4 !text-inherit" />
                  Add effective till date
                </Button>
              )
              : (
                <Wrapper
                  id="effective-till"
                  error={invalid}
                  errorText={error?.message?.toString()}
                  showErrorText={false}
                  label="Effective till"
                  className="w-1/2"
                >
                  <DatePicker
                    {...field}
                    value={field.value ?? null}
                    className="flex-1"
                    containerClassName="w-full"
                    inputContainerClassName="w-full"
                    showDateButtons={false}
                    disableFuture
                    maxDate={getOrgNow().startOf('day')}
                    onChange={(value) => {
                      field.onChange(value);
                      if (isNil(value)) {
                        setValue('inactive', false, { shouldDirty: true });
                      } else {
                        setValue('inactive', true, { shouldDirty: true });
                      }
                      triggerEffectiveDateValidation();
                    }}
                  />
                  <ToolTip title="Remove effective till date">
                    <IconButton
                      size="small"
                      className="!h-6 !mt-1"
                      onClick={() => {
                        setValue('effectiveTill', null, {
                          shouldDirty: true,
                        });
                        setValue('inactive', false, { shouldDirty: true });
                        triggerEffectiveDateValidation();
                      }}
                    >
                      <CloseIcon className="!w-4 !h-4 !text-inherit" />
                    </IconButton>
                  </ToolTip>
                </Wrapper>
              )
          )}
        />
      </div>
      <div className="flex gap-form-field items-center">
        <Controller
          name="insuranceSlot"
          control={control}
          rules={{ required: 'Billing order is required' }}
          render={({ field, fieldState: { invalid } }) => (
            <Select<InsuranceSlot>
              {...field}
              error={invalid}
              showErrorText={false}
              getValue={(option) => option}
              getLabel={(option) => getInsuranceSlotLabel(option)}
              options={insuranceSlotValues}
              placeholder="Select"
              className="w-[240px]"
              id="insurance-slot"
              muiClasses={{ select: '!py-[7px]' }}
              label="Billing Order"
              required
            />
          )}
        />
        <Divider orientation="vertical" flexItem />
        <Controller
          name="inactive"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              id="insurance-activation-toggle"
              checked={Boolean(!field.value)}
              onChange={(value) => {
                const isActive = Boolean(value);
                if (isActive) {
                  field.onChange(false);
                  setValue('effectiveTill', null, { shouldDirty: true });
                } else {
                  field.onChange(true);
                  setValue('effectiveTill', getDefaultInactiveEffectiveTill(), { shouldDirty: true });
                }
                triggerEffectiveDateValidation();
              }}
              value={(!field?.value).toString()}
              label="Active"
            />
          )}
        />
      </div>
      <Controller
        name="notes"
        control={control}
        render={
          ({ field }) => (
            <TextAreaWithActions
              {...field}
              value={field?.value?.toString() ?? ''}
              label="Notes"
              labelClassName="font-semibold text-xs text-gray-500"
              id="insurance-notes"
              placeholder="Enter notes here"
              trackingLabel="Insurance Notes"
              rows={2}
            />
          )
        }
      />
    </div>
  );
}
